import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertCircle, DollarSign } from "lucide-react";
import { toast } from "sonner";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function TreasurerPaymentConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [paymentDate, setPaymentDate] = useState(todayStr());
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Missing token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    if (!user) {
      // Redirect to login, preserve the original URL
      const back = encodeURIComponent(`/treasurer/payment-confirm?token=${token}`);
      navigate(`/login?redirect=${back}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/treasurer/payment/lookup`, { params: { token } });
        setInfo(res.data);
        if (res.data.already_paid) setDone(res.data.payment);
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not load this link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, user, navigate]);

  const submit = async () => {
    if (!paymentDate) {
      toast.error("Please pick a payment date");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/treasurer/payment/confirm`,
        { payment_date: paymentDate, reference: reference || null, notes: notes || null },
        { params: { token } }
      );
      setDone(res.data.payment);
      toast.success("Payment confirmed - thank you!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 pt-16">
      <div className="w-full max-w-lg" data-testid="treasurer-confirm-page">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mb-3">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Confirmation</h1>
          <p className="text-sm text-gray-500 mt-1">Signed in as {user?.email}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {loading && <p className="text-gray-500 text-center py-6">Loading…</p>}

          {error && (
            <div className="flex items-start gap-3 text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Cannot confirm payment</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {info && !error && (
            <>
              <div className="space-y-2 pb-4 mb-4 border-b border-gray-100">
                <Row label="Job #" value={info.job_number} />
                <Row label="Location" value={info.restaurant_name} />
                <Row label="Total Cost" value={`$${Number(info.total_cost).toFixed(2)}`} />
                <Row label="Treasurer" value={info.treasurer_email} />
                <Row label="Status" value={info.status} />
              </div>

              {done ? (
                <div className="flex items-start gap-3 text-green-800 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="treasurer-confirmed-banner">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">Payment confirmed</p>
                    <p>Date: {done.payment_date}</p>
                    {done.reference && <p>Reference: {done.reference}</p>}
                    {done.confirmed_at && (
                      <p className="text-xs text-green-700 mt-1">
                        Recorded {new Date(done.confirmed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment_date">Payment Date *</Label>
                    <Input
                      id="payment_date"
                      type="date"
                      value={paymentDate}
                      max={todayStr()}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      data-testid="payment-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference">Reference / Check # (optional)</Label>
                    <Input
                      id="reference"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g. Check #4521 or ACH ID"
                      data-testid="payment-reference-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="payment-notes-input"
                    />
                  </div>
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="payment-submit-btn"
                  >
                    {submitting ? "Confirming…" : "Confirm Payment"}
                  </Button>
                </div>
              )}
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/select-market" className="text-sm text-gray-500 hover:text-gray-700">
              Back to Efacilicare
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right">{value}</span>
    </div>
  );
}
